/**
 * BootstrapUpload.jsx
 *
 * data-fit LLC — DecisionFlow
 * Stage 1 LOAD — Screen LOAD-01: Bootstrap Upload
 * Branch: feature/compliance-lifecycle-foundation
 *
 * First screen the RevOps operator sees after tenant provisioning.
 * Presents the tier-appropriate CSV checklist, accepts file uploads,
 * and triggers pre-ingestion validation. No business data writes occur
 * on this screen — validate call writes import_log lineage rows only.
 *
 * AI (CSV Validator) fires after validate returns using validate response
 * data to construct the deterministic payload. isValidating clears on
 * validate response; AI tracked independently via aiStatus.
 *
 * Spec authority: ComponentSpec_LOAD_corrected_S86g.md
 * Path: ui/src/components/tenant-admin/BootstrapUpload.jsx
 * M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';

const API_BASE_URL = '/api/v1';

const FILE_SIZE_LIMIT_MB = 10;
const ROW_LIMIT_PER_FILE = 10000;
const AI_TIMEOUT_MS = 10000;

const FILE_DEFINITIONS = [
  {
    type: 'company_setup',
    filename: 'company_setup.csv',
    label: 'Company setup',
    tiers: ['essentials', 'standard', 'enterprise'],
  },
  {
    type: 'roles_motions',
    filename: 'roles_motions.csv',
    label: 'Roles & selling motions',
    tiers: ['essentials', 'standard', 'enterprise'],
  },
  {
    type: 'positions',
    filename: 'positions.csv',
    label: 'Positions',
    tiers: ['essentials', 'standard', 'enterprise'],
  },
  {
    type: 'comp_admins',
    filename: 'comp_admins.csv',
    label: 'Comp admins',
    tiers: ['standard', 'enterprise'],
  },
  {
    type: 'geo_hierarchy',
    filename: 'geo_hierarchy.csv',
    label: 'Geo hierarchy',
    tiers: ['enterprise'],
  },
  {
    type: 'fx_rates',
    filename: 'fx_rates.csv',
    label: 'FX rates',
    tiers: ['enterprise'],
  },
];

const INITIAL_FILES = {
  company_setup: null,
  roles_motions: null,
  positions: null,
  comp_admins: null,
  geo_hierarchy: null,
  fx_rates: null,
};

// Status badge classes — M040 tokens
const statusClasses = {
  idle: 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] ring-[rgb(var(--color-border))]',
  uploaded: 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))] ring-[rgb(var(--color-success))]/30',
  invalid: 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))] ring-[rgb(var(--color-danger))]/30',
};

function normalizeTier(tier) {
  return String(tier || '').toLowerCase();
}

function getRequiredFileDefinitions(tier) {
  const normalizedTier = normalizeTier(tier);
  return FILE_DEFINITIONS.filter((definition) => definition.tiers.includes(normalizedTier));
}

function buildTopErrors(errors = []) {
  const grouped = errors.reduce((acc, error) => {
    if (!error?.code) return acc;
    acc[error.code] = (acc[error.code] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(grouped)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([code, affected_rows]) => ({ code, affected_rows }));
}

function timeoutPromise(ms) {
  return new Promise((_, reject) => {
    window.setTimeout(() => {
      reject(new Error('AI_TIMEOUT'));
    }, ms);
  });
}

function Toast({ toast, onDismiss }) {
  if (!toast) return null;

  const isError = toast.type === 'error';

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed right-6 top-6 z-50 max-w-md rounded-2xl border px-4 py-3 shadow-lg ${
        isError
          ? 'border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]'
          : 'border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]'
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-tok-sm font-semibold">{isError ? 'Error' : 'Done'}</span>
        <p className="text-tok-sm">{toast.message}</p>
        <button
          type="button"
          onClick={onDismiss}
          className="ml-2 rounded-md px-1 text-tok-sm font-semibold hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
          aria-label="Dismiss notification"
        >
          ×
        </button>
      </div>
    </div>
  );
}

function FullScreenModal({ title, message, primaryLabel, secondaryLabel, onPrimary, onSecondary, destructive }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[rgb(var(--color-text))]/50 px-4">
      <div className="w-full max-w-xl rounded-3xl bg-[rgb(var(--color-surface))] p-6 shadow-2xl">
        <div
          className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${
            destructive
              ? 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]'
              : 'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]'
          }`}
        >
          !
        </div>
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">{title}</h2>
        <p className="mt-3 text-tok-sm leading-6 text-[rgb(var(--color-text-muted))]">{message}</p>
        <div className="mt-6 flex justify-end gap-3">
          {secondaryLabel && (
            <button
              type="button"
              onClick={onSecondary}
              className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
            >
              {secondaryLabel}
            </button>
          )}
          {primaryLabel && (
            <button
              type="button"
              onClick={onPrimary}
              className={`rounded-xl px-4 py-2 text-tok-sm font-semibold text-white focus:outline-none ${
                destructive
                  ? 'bg-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/80'
                  : 'bg-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary-hover))]'
              }`}
            >
              {primaryLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CopilotPanel({ aiStatus, uploadedCount, onRetry }) {
  let content = null;

  if (aiStatus === 'loading') {
    content = (
      <div className="space-y-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[rgb(var(--color-border))] border-t-[rgb(var(--color-text))]" />
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Scanning validation results for grouped fixes.</p>
      </div>
    );
  } else if (aiStatus === 'error') {
    content = (
      <div className="space-y-3">
        <p className="text-tok-sm text-[rgb(var(--color-danger))]">AI guidance could not be generated.</p>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-xl bg-[rgb(var(--color-primary))] px-3 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] focus:outline-none"
        >
          Retry AI guidance
        </button>
      </div>
    );
  } else if (aiStatus === 'timeout') {
    content = (
      <div className="space-y-3">
        <p className="text-tok-sm text-[rgb(var(--color-warning))]">AI guidance timed out. You can continue without it or retry.</p>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-xl bg-[rgb(var(--color-primary))] px-3 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] focus:outline-none"
        >
          Retry AI guidance
        </button>
      </div>
    );
  } else if (aiStatus === 'blocked') {
    content = (
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">AI guidance is unavailable for this request.</p>
    );
  } else if (uploadedCount > 0) {
    content = (
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        File received. I&apos;ll validate this when you trigger validation.
      </p>
    );
  } else {
    content = (
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        I&apos;ll scan your CSVs for schema errors, missing fields, and data inconsistencies before ingestion begins.
      </p>
    );
  }

  return (
    <aside className="rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 shadow-sm">
      <div className="mb-4">
        <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Copilot</p>
        <h2 className="mt-1 text-tok-lg font-semibold text-[rgb(var(--color-text))]">CSV Validator</h2>
      </div>
      {content}
    </aside>
  );
}

export default function BootstrapUpload({
  tenantId,
  tier,
  reloadMode,
  aiStatus,
  onValidationComplete,
  onAiStatusChange,
  onAiResponseReceived,
}) {
  const { token, user } = useAuth();
  const fileInputRefs = useRef({});

  const [files, setFiles] = useState(INITIAL_FILES);
  const [fileStatuses, setFileStatuses] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [toast, setToast] = useState(null);
  const [reloadModal, setReloadModal] = useState(null);

  const requiredFiles = useMemo(() => getRequiredFileDefinitions(tier), [tier]);
  const requiredFileTypes = useMemo(() => requiredFiles.map((file) => file.type), [requiredFiles]);

  const uploadedCount = useMemo(
    () => requiredFileTypes.filter((fileType) => files[fileType]).length,
    [files, requiredFileTypes]
  );

  const showToast = useCallback((nextToast) => {
    setToast(nextToast);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const validateFileClientSide = useCallback(async (file) => {
    if (!file) {
      return { valid: false, message: 'Required file is missing.' };
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      return { valid: false, message: 'File must be .csv format.' };
    }

    const sizeLimitBytes = FILE_SIZE_LIMIT_MB * 1024 * 1024;
    if (file.size > sizeLimitBytes) {
      return {
        valid: false,
        message: `File exceeds maximum size of ${FILE_SIZE_LIMIT_MB} MB. Split into smaller files.`,
      };
    }

    const text = await file.text();
    const firstLine = text.split(/\r?\n/).find((line) => line.trim().length > 0);

    if (!firstLine || !firstLine.includes(',')) {
      return { valid: false, message: 'File must contain parseable CSV headers.' };
    }

    const rowCount = text.split(/\r?\n/).filter((line) => line.trim().length > 0).length - 1;
    if (rowCount > ROW_LIMIT_PER_FILE) {
      return {
        valid: false,
        message: `File exceeds maximum row limit of ${ROW_LIMIT_PER_FILE}. Split into smaller files.`,
      };
    }

    return { valid: true, message: null };
  }, []);

  const recomputeClientFileState = useCallback(
    async (nextFiles) => {
      const nextStatuses = {};
      const nextErrors = {};

      await Promise.all(
        requiredFileTypes.map(async (fileType) => {
          const file = nextFiles[fileType];

          if (!file) {
            nextStatuses[fileType] = 'idle';
            nextErrors[fileType] = `Required for ${tier} tier`;
            return;
          }

          const result = await validateFileClientSide(file);
          nextStatuses[fileType] = result.valid ? 'uploaded' : 'invalid';
          nextErrors[fileType] = result.message;
        })
      );

      setFileStatuses(nextStatuses);
      setFileErrors(nextErrors);

      return { nextStatuses, nextErrors };
    },
    [requiredFileTypes, tier, validateFileClientSide]
  );

  const handleFileChange = useCallback(
    async (fileType, event) => {
      const file = event.target.files?.[0] || null;
      const nextFiles = { ...files, [fileType]: file };

      setFiles(nextFiles);
      setValidationError(null);
      await recomputeClientFileState(nextFiles);

      if (file) {
        showToast({ type: 'success', message: `${file.name} uploaded.` });
      }
    },
    [files, recomputeClientFileState, showToast]
  );

  const handleReplaceClick = useCallback((fileType) => {
    fileInputRefs.current[fileType]?.click();
  }, []);

  const buildFormData = useCallback(() => {
    const formData = new FormData();

    requiredFileTypes.forEach((fileType) => {
      const file = files[fileType];
      if (file) {
        formData.append(fileType, file);
      }
    });

    return formData;
  }, [files, requiredFileTypes]);

  const normalizeValidationResponse = useCallback((payload) => {
    return {
      ...payload,
      bootstrapBatchId: payload.bootstrap_batch_id,
      validationId: payload.validation_id,
      status: payload.validation_status,
      summary: payload.summary || {
        total_rows: 0,
        rows_valid: 0,
        rows_warning: 0,
        rows_critical: 0,
      },
      errors: Array.isArray(payload.errors) ? payload.errors : [],
    };
  }, []);

  const postAiQuery = useCallback(
    async (validationPayload) => {
      const inputData = {
        bootstrap_batch_id: validationPayload.bootstrap_batch_id,
        tier,
        files_received: validationPayload.files_received || [],
        error_summary: {
          critical: validationPayload.summary?.rows_critical || 0,
          warning: validationPayload.summary?.rows_warning || 0,
          valid: validationPayload.summary?.rows_valid || 0,
        },
        top_errors: buildTopErrors(validationPayload.errors || []),
      };

      const response = await Promise.race([
        fetch(`${API_BASE_URL}/ai-queries`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            panel_context: 'load.csv_validation',
            input_data: inputData,
          }),
        }),
        timeoutPromise(AI_TIMEOUT_MS),
      ]);

      if (!response.ok) {
        throw new Error(`AI_QUERY_${response.status}`);
      }

      const payload = await response.json();

      if (payload.query_blocked === true) {
        onAiStatusChange('blocked');
        return;
      }

      onAiResponseReceived(payload);
    },
    [tier, token, onAiStatusChange, onAiResponseReceived]
  );

  const retryAiWithCurrentValidation = useCallback(() => {
    if (!validationError?.validationPayload) return;

    onAiStatusChange('loading');

    postAiQuery(validationError.validationPayload).catch((error) => {
      if (error.message === 'AI_TIMEOUT') {
        onAiStatusChange('timeout');
        return;
      }

      onAiStatusChange('error');
    });
  }, [onAiStatusChange, postAiQuery, validationError]);

  const handleValidate = useCallback(async () => {
    setValidationError(null);

    const { nextStatuses, nextErrors } = await recomputeClientFileState(files);
    const hasMissingOrInvalid = requiredFileTypes.some(
      (fileType) => !files[fileType] || nextStatuses[fileType] === 'invalid'
    );

    if (hasMissingOrInvalid) {
      setValidationError({
        message: 'Upload all required CSV files and resolve client-side file errors before validation.',
      });
      return;
    }

    setIsValidating(true);
    onAiStatusChange('loading');

    const formData = buildFormData();

    const validatePromise = fetch(`${API_BASE_URL}/bootstrap-import/validate`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    let validationPayload = null;

    try {
      const validateResponse = await validatePromise;

      if (!validateResponse.ok) {
        let errorPayload = {};
        try {
          errorPayload = await validateResponse.json();
        } catch {
          errorPayload = {};
        }

        const code = errorPayload.code || errorPayload.error_code;

        if (code === 'MIXED_PERIOD_TYPES') {
          showToast({
            type: 'error',
            message: 'company_setup.csv contains mixed period types. All rows must share the same period_type.',
          });
          return;
        }

        if (code === 'TENANT_ALREADY_INITIALIZED') {
          const blocked =
            errorPayload.reload_blocked === true ||
            errorPayload.code === 'RELOAD_BLOCKED_PERIOD_ACTIVE' ||
            errorPayload.error_code === 'RELOAD_BLOCKED_PERIOD_ACTIVE';

          setReloadModal(
            blocked
              ? {
                  title: 'Re-load is no longer permitted',
                  message:
                    'This tenant has already been activated. Re-loading data is no longer permitted. Contact your platform administrator.',
                  blocked: true,
                }
              : {
                  title: 'Existing data detected',
                  message:
                    'This tenant already has data loaded. Proceeding will overwrite all existing reference data. This cannot be undone once you proceed past Stage 2 — Configure.',
                  blocked: false,
                }
          );
          return;
        }

        if (code === 'RELOAD_BLOCKED_PERIOD_ACTIVE') {
          setReloadModal({
            title: 'Re-load is no longer permitted',
            message:
              'This tenant has already been activated. Re-loading data is no longer permitted. Contact your platform administrator.',
            blocked: true,
          });
          return;
        }

        if (code === 'AUTH_INSUFFICIENT_ROLE') {
          setValidationError({
            blocking: true,
            message: 'You do not have permission to perform bootstrap import.',
          });
          return;
        }

        if (code === 'FILE_TOO_LARGE') {
          setValidationError({
            message: `File exceeds maximum size of ${FILE_SIZE_LIMIT_MB} MB. Split into smaller files.`,
          });
          return;
        }

        if (code === 'ROW_LIMIT_EXCEEDED') {
          setValidationError({
            message: `File exceeds maximum row limit of ${ROW_LIMIT_PER_FILE}. Split into smaller files.`,
          });
          return;
        }

        setValidationError({
          message: errorPayload.message || 'Validation failed. Review the uploaded files and try again.',
        });
        showToast({ type: 'error', message: 'Validation failed. Try again.' });
        return;
      }

      validationPayload = await validateResponse.json();
      const normalized = normalizeValidationResponse(validationPayload);

      setValidationError({ validationPayload });
      postAiQuery(validationPayload).catch((error) => {
        if (error.message === 'AI_TIMEOUT') {
          onAiStatusChange('timeout');
          return;
        }

        onAiStatusChange('error');
      });

      onValidationComplete(normalized);
    } catch {
      setValidationError({ message: 'Validation service is unavailable. Try again.' });
      onAiStatusChange('error');
      showToast({ type: 'error', message: 'Validation service is unavailable. Try again.' });
    } finally {
      setIsValidating(false);
    }
  }, [
    buildFormData,
    files,
    normalizeValidationResponse,
    onAiStatusChange,
    onValidationComplete,
    postAiQuery,
    recomputeClientFileState,
    requiredFileTypes,
    showToast,
    token,
  ]);

  const allRequiredUploaded = requiredFileTypes.every((fileType) => Boolean(files[fileType]));
  const hasInvalidFiles = requiredFileTypes.some((fileType) => fileStatuses[fileType] === 'invalid');
  const canValidate = allRequiredUploaded && !hasInvalidFiles && !isValidating;

  if (validationError?.blocking) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <div className="max-w-lg rounded-3xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-surface))] p-6 shadow-sm">
          <h1 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Access blocked</h1>
          <p className="mt-3 text-tok-sm leading-6 text-[rgb(var(--color-text-muted))]">{validationError.message}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-7xl flex-col px-6 py-6">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      {reloadModal && (
        <FullScreenModal
          title={reloadModal.title}
          message={reloadModal.message}
          destructive
          primaryLabel={reloadModal.blocked ? 'Cancel' : 'Continue to Re-load'}
          secondaryLabel={reloadModal.blocked ? null : 'Cancel'}
          onPrimary={() => setReloadModal(null)}
          onSecondary={() => setReloadModal(null)}
        />
      )}

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4 rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 shadow-sm">
        <div>
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">
            {user?.tenant_name || tenantId || 'Tenant'}
          </p>
          <h1 className="mt-1 text-tok-2xl font-semibold text-[rgb(var(--color-text))]">Stage 1 — Bootstrap Upload</h1>
        </div>
        <span className="rounded-full bg-[rgb(var(--color-primary))] px-3 py-1 text-tok-sm font-semibold capitalize text-white">
          {tier}
        </span>
      </header>

      {reloadMode && (
        <div className="mb-6 rounded-2xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-4 py-3 text-tok-sm font-medium text-[rgb(var(--color-warning))]">
          You are replacing all existing reference data. This cannot be undone after activation in Stage 2.
        </div>
      )}

      <div className="grid flex-1 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <section className="rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 shadow-sm">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-tok-lg font-semibold text-[rgb(var(--color-text))]">CSV Upload Checklist</h2>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
                Upload the required CSV files for the current subscription tier.
              </p>
            </div>
            <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-3 py-1 text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
              {uploadedCount}/{requiredFileTypes.length} uploaded
            </span>
          </div>

          <div className="space-y-3">
            {requiredFiles.map((definition) => {
              const file = files[definition.type];
              const status = fileStatuses[definition.type] || 'idle';
              const error = fileErrors[definition.type];

              return (
                <div
                  key={definition.type}
                  className={`rounded-2xl border p-4 ${
                    status === 'invalid' || (!file && error)
                      ? 'border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5'
                      : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]'
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-tok-xs font-bold ${
                            file
                              ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]'
                              : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
                          }`}
                          aria-hidden="true"
                        >
                          {file ? '✓' : '□'}
                        </span>
                        <div>
                          <p className="font-semibold text-[rgb(var(--color-text))]">{definition.filename}</p>
                          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">{definition.label}</p>
                        </div>
                      </div>
                      {file && <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">{file.name}</p>}
                      {error && (
                        <p className={`mt-2 text-tok-sm ${status === 'invalid' ? 'text-[rgb(var(--color-danger))]' : 'text-[rgb(var(--color-text-muted))]'}`}>
                          {error}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <span
                        className={`rounded-full px-3 py-1 text-tok-xs font-semibold ring-1 ${
                          statusClasses[status] || statusClasses.idle
                        }`}
                      >
                        {status}
                      </span>

                      <input
                        ref={(node) => {
                          fileInputRefs.current[definition.type] = node;
                        }}
                        type="file"
                        accept=".csv,text/csv"
                        className="hidden"
                        onChange={(event) => handleFileChange(definition.type, event)}
                      />

                      <button
                        type="button"
                        onClick={() => handleReplaceClick(definition.type)}
                        className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
                      >
                        {file ? 'Replace' : 'Upload'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 rounded-2xl border border-dashed border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
            <button
              type="button"
              disabled
              className="text-tok-sm font-semibold text-[rgb(var(--color-text-muted))]"
              title="Template download is pending OI-BOOTSTRAP-TEMPLATE-DOWNLOAD."
            >
              Download templates ↓
            </button>
            <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Template ZIP download is unavailable until the template contract is enabled.
            </p>
          </div>

          {validationError?.message && (
            <div className="mt-5 rounded-2xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {validationError.message}
            </div>
          )}
        </section>

        <CopilotPanel aiStatus={aiStatus} uploadedCount={uploadedCount} onRetry={retryAiWithCurrentValidation} />
      </div>

      <footer className="mt-6 flex items-center justify-between rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 shadow-sm">
        <button
          type="button"
          className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
        >
          Cancel
        </button>

        <button
          type="button"
          disabled={!canValidate}
          onClick={handleValidate}
          className="rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:cursor-not-allowed disabled:bg-[rgb(var(--color-disabled))] focus:outline-none"
        >
          {isValidating ? 'Validating…' : 'Validate Files →'}
        </button>
      </footer>
    </main>
  );
}
